import type { OnInit } from '@angular/core';
import { Component, Input, ViewChild, ViewContainerRef } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  NgSelectComponent,
  NgOptionTemplateDirective,
} from '@ng-select/ng-select';
import { Store } from '@ngrx/store';
import {
  FocusAction,
  FormControlState,
  MarkAsDirtyAction,
  MarkAsTouchedAction,
  SetValueAction,
  UnfocusAction,
} from 'ngrx-forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { FormsModule } from '@angular/forms';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import {
  ValidationClassesDirective,
  IsInvalidPipe,
} from '@innogy/utils-deprecated';

import type { DropdownItem, DropdownItemValue } from './dropdown.model';

@Component({
  selector: 'wl-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: [
    './dropdown.component.essent.scss',
    './dropdown.component.ed.scss',
  ],
  standalone: true,
  imports: [
    AutomationIdDirective,
    FaIconComponent,
    NgSelectComponent,
    AutomationIdDirective,
    ValidationClassesDirective,
    IsInvalidPipe,
    FormsModule,
    NgOptionTemplateDirective,
  ],
})
export class DropdownComponent implements OnInit {
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() formsControl?: FormControlState<DropdownItemValue | undefined>;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() icon?: IconProp;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() placeholder?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() label?: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() labelForId = 'none';
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() clearable = false;
  // floatingLabel = true is the default for dropdowns
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() floatingLabel = true;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() omitValidationClasses?: string[];

  @ViewChild('ngSelectComponent')
  public ngSelect!: NgSelectComponent;

  currentValue: DropdownItemValue = null;
  selectedOption?: DropdownItem;

  get anyIcons() {
    return this.dropdownItems?.some((item) => item.icon);
  }

  private _dropdownItems: DropdownItem[] | null = null;
  get dropdownItems() {
    return this._dropdownItems;
  }
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() set dropdownItems(value: DropdownItem[] | null | undefined) {
    this._dropdownItems = value ?? null;
    if (this.currentValue != null && !this.isInDropdown(this.currentValue)) {
      this.currentValue = null;
      this.setValue(this.currentValue);
    }
  }

  constructor(
    private readonly store$: Store<any>,
    private readonly viewContainerRef: ViewContainerRef
  ) {}

  ngOnInit(): void {
    if (this.placeholder && this.label && this.floatingLabel) {
      this.placeholder = undefined;
    }

    if (
      typeof this.formsControl?.value === 'string' &&
      this.isInDropdown(this.formsControl?.value)
    ) {
      this.currentValue = this.formsControl?.value;
    }
  }

  public onOpen() {
    if (this.formsControl != null) {
      this.store$.dispatch(new FocusAction(this.formsControl.id));
      this.dispatchNativeEvent('focus');
    }
  }

  public onClose() {
    if (this.formsControl != null) {
      this.store$.dispatch(new UnfocusAction(this.formsControl.id));
      this.touchControl(this.formsControl);
    }
  }

  public onSelect(option: DropdownItem | undefined) {
    this.selectedOption = option;
    this.setValue(option?.value as DropdownItemValue);
  }

  private setValue(value: DropdownItemValue) {
    if (this.formsControl != null) {
      this.store$.dispatch(new SetValueAction(this.formsControl.id, value));
      this.touchControl(this.formsControl);
      this.store$.dispatch(new UnfocusAction(this.formsControl.id));
      this.dispatchNativeEvent('change');
    }
  }

  private isInDropdown(dropdownItemValue: DropdownItemValue) {
    return !!this.dropdownItems
      ?.map(({ value }) => value)
      .includes(dropdownItemValue);
  }

  private touchControl(
    control: FormControlState<DropdownItemValue | undefined>
  ) {
    this.store$.dispatch(new MarkAsTouchedAction(control.id));
    this.store$.dispatch(new MarkAsDirtyAction(control.id));
  }

  private dispatchNativeEvent(eventName: string) {
    this.viewContainerRef.element.nativeElement.dispatchEvent(
      new Event(eventName)
    );
  }
}
